/* Archadia collection cosmos: starfield backdrop, floating product cards, hero orbit, load more. */
(() => {
  if (window.__czBoot) { window.__czBoot(); return; }
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------- starfield ---------- */
  function initSky(canvas) {
    if (!canvas || canvas.__czInit) return;
    canvas.__czInit = true;
    const ctx = canvas.getContext('2d');
    const accent = getComputedStyle(canvas).getPropertyValue('--cz-accent').trim() || '#2f9db8';
    const density = parseFloat(canvas.dataset.density || '1');
    let W = 0, H = 0, dpr = 1, stars = [], shooting = [];
    let mx = 0, my = 0, tmx = 0, tmy = 0, warp = 0, lastY = window.scrollY, running = true;

    const hexToRgb = (h) => { h = h.replace('#', ''); if (h.length === 3) h = h.split('').map((c) => c + c).join(''); const n = parseInt(h, 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
    const [ar, ag, ab] = hexToRgb(accent);

    function resize() {
      dpr = Math.min(1.5, window.devicePixelRatio || 1);
      W = window.innerWidth; H = window.innerHeight;
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.round(Math.min(420, (W * H) / 4200) * density);
      stars = [];
      for (let i = 0; i < count; i++) {
        const z = Math.random();           // 0 far .. 1 near
        stars.push({
          x: Math.random() * W, y: Math.random() * H, z,
          r: 0.35 + z * 1.25,
          tw: Math.random() * Math.PI * 2, ts: 0.6 + Math.random() * 1.8,
          tint: Math.random() < 0.12
        });
      }
    }
    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', (e) => { tmx = e.clientX / W - 0.5; tmy = e.clientY / H - 0.5; }, { passive: true });
    window.addEventListener('scroll', () => {
      const y = window.scrollY; const dv = y - lastY; lastY = y;
      warp = Math.max(-60, Math.min(60, warp + dv * 0.35));
    }, { passive: true });
    document.addEventListener('visibilitychange', () => { running = !document.hidden; if (running) requestAnimationFrame(tick); });

    let prev = performance.now(), nextShoot = prev + 2500;
    function tick(now) {
      if (!running) return;
      const dt = Math.min(0.05, (now - prev) / 1000); prev = now;
      mx += (tmx - mx) * 0.04; my += (tmy - my) * 0.04;
      warp *= 0.9;
      ctx.clearRect(0, 0, W, H);
      const streak = reduce ? 0 : warp;
      for (const s of stars) {
        if (!reduce) {
          s.y -= (4 + s.z * 10) * dt;                 // slow upward drift
          s.y -= streak * s.z * 0.5;                  // scroll warp
          if (s.y < -10) { s.y = H + 10; s.x = Math.random() * W; }
          if (s.y > H + 10) { s.y = -10; s.x = Math.random() * W; }
          s.tw += s.ts * dt;
        }
        const px = s.x + mx * 30 * s.z, py = s.y + my * 20 * s.z;
        const a = (0.25 + s.z * 0.65) * (0.65 + 0.35 * Math.sin(s.tw));
        const col = s.tint ? 'rgba(' + ar + ',' + ag + ',' + ab + ',' + a.toFixed(3) + ')' : 'rgba(255,255,255,' + a.toFixed(3) + ')';
        const len = Math.abs(streak) * s.z * 0.9;
        if (len > 1.2) {
          ctx.strokeStyle = col; ctx.lineWidth = s.r; ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px, py + (streak > 0 ? len : -len)); ctx.stroke();
        } else {
          ctx.fillStyle = col; ctx.beginPath(); ctx.arc(px, py, s.r, 0, Math.PI * 2); ctx.fill();
        }
      }
      // shooting stars
      if (!reduce && now > nextShoot) {
        nextShoot = now + 3500 + Math.random() * 6000;
        const fromLeft = Math.random() < 0.5;
        shooting.push({ x: fromLeft ? Math.random() * W * 0.5 : W * 0.5 + Math.random() * W * 0.5, y: Math.random() * H * 0.45, vx: (fromLeft ? 1 : -1) * (520 + Math.random() * 380), vy: 220 + Math.random() * 160, life: 1 });
      }
      for (let i = shooting.length - 1; i >= 0; i--) {
        const s = shooting[i];
        s.x += s.vx * dt; s.y += s.vy * dt; s.life -= dt * 0.9;
        if (s.life <= 0) { shooting.splice(i, 1); continue; }
        const tail = 0.12;
        const g = ctx.createLinearGradient(s.x, s.y, s.x - s.vx * tail, s.y - s.vy * tail);
        g.addColorStop(0, 'rgba(255,255,255,' + (0.9 * s.life).toFixed(3) + ')');
        g.addColorStop(0.3, 'rgba(' + ar + ',' + ag + ',' + ab + ',' + (0.45 * s.life).toFixed(3) + ')');
        g.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.strokeStyle = g; ctx.lineWidth = 1.4; ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.lineTo(s.x - s.vx * tail, s.y - s.vy * tail); ctx.stroke();
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* ---------- hero ---------- */
  function initHero(root) {
    if (!root || root.__czInit) return;
    root.__czInit = true;
    const title = root.querySelector('[data-cz-title]');
    if (title && !title.dataset.split) {
      title.dataset.split = '1';
      const text = title.textContent.trim();
      title.setAttribute('aria-label', text);
      title.innerHTML = '';
      let i = 0;
      text.split(' ').forEach((word, wi, arr) => {
        const w = document.createElement('span');
        w.className = 'cz-word'; w.setAttribute('aria-hidden', 'true');
        if (/\p{Extended_Pictographic}/u.test(word) && !/[\p{L}\p{N}]/u.test(word)) w.classList.add('cz-emoji');
        Array.from(word).forEach((ch) => {
          const s = document.createElement('span');
          s.className = 'cz-ch'; s.textContent = ch; s.style.setProperty('--i', i++);
          w.appendChild(s);
        });
        title.appendChild(w);
        if (wi < arr.length - 1) title.appendChild(document.createTextNode(' '));
      });
    }
    requestAnimationFrame(() => root.classList.add('cz-in'));

    // description toggle
    const more = root.querySelector('[data-cz-more]');
    if (more) more.addEventListener('click', () => {
      const d = root.querySelector('[data-cz-desc]');
      const open = d.classList.toggle('is-open');
      more.textContent = open ? more.dataset.less : more.dataset.more;
    });

    // orbit parallax
    const orb = root.querySelector('[data-cz-orbit]');
    if (orb && finePointer && !reduce) {
      let tx = 0, ty = 0, x = 0, y = 0, on = false;
      root.addEventListener('pointermove', (e) => {
        const r = root.getBoundingClientRect();
        tx = (e.clientX - r.left) / r.width - 0.5; ty = (e.clientY - r.top) / r.height - 0.5;
        if (!on) { on = true; requestAnimationFrame(loop); }
      });
      function loop() {
        x += (tx - x) * 0.08; y += (ty - y) * 0.08;
        orb.style.transform = 'perspective(1000px) rotateY(' + (x * 14).toFixed(2) + 'deg) rotateX(' + (-y * 10).toFixed(2) + 'deg) translate3d(' + (x * 18).toFixed(1) + 'px,' + (y * 14).toFixed(1) + 'px,0)';
        if (Math.abs(tx - x) > 0.001 || Math.abs(ty - y) > 0.001) requestAnimationFrame(loop); else on = false;
      }
    }
  }

  /* ---------- cards ---------- */
  let revealObs = null;
  function getRevealObs() {
    if (revealObs || !('IntersectionObserver' in window)) return revealObs;
    revealObs = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        const el = en.target;
        el.classList.add('cz-shown');
        revealObs.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    return revealObs;
  }

  function initCard(cell, k) {
    if (cell.__czInit) return;
    cell.__czInit = true;
    cell.style.setProperty('--cz-delay', ((k % 4) * 90) + 'ms');
    cell.style.setProperty('--cz-float', (5.5 + (k * 1.37) % 2.5).toFixed(2) + 's');
    cell.style.setProperty('--cz-phase', (-(k * 0.83) % 4).toFixed(2) + 's');
    const obs = getRevealObs();
    if (obs && !reduce) obs.observe(cell); else cell.classList.add('cz-shown');

    if (!finePointer || reduce) return;
    const tilt = cell.querySelector('.cz-tilt');
    if (!tilt) return;
    let tx = 0, ty = 0, x = 0, y = 0, hover = false, on = false;
    cell.addEventListener('pointerenter', () => { hover = true; cell.classList.add('is-hover'); if (!on) { on = true; requestAnimationFrame(loop); } });
    cell.addEventListener('pointerleave', () => { hover = false; tx = 0; ty = 0; cell.classList.remove('is-hover'); if (!on) { on = true; requestAnimationFrame(loop); } });
    cell.addEventListener('pointermove', (e) => {
      const r = tilt.getBoundingClientRect();
      tx = (e.clientX - r.left) / r.width - 0.5; ty = (e.clientY - r.top) / r.height - 0.5;
      tilt.style.setProperty('--gx', ((tx + 0.5) * 100).toFixed(1) + '%');
      tilt.style.setProperty('--gy', ((ty + 0.5) * 100).toFixed(1) + '%');
    });
    function loop() {
      x += (tx - x) * 0.12; y += (ty - y) * 0.12;
      tilt.style.transform = 'perspective(900px) rotateY(' + (x * 16).toFixed(2) + 'deg) rotateX(' + (-y * 14).toFixed(2) + 'deg) translateY(' + (hover ? -8 : 0) + 'px)';
      if (hover || Math.abs(x) > 0.002 || Math.abs(y) > 0.002) requestAnimationFrame(loop);
      else { tilt.style.transform = ''; on = false; }
    }
  }

  function initGrid(root) {
    if (!root || root.__czInit) return;
    root.__czInit = true;
    const grid = root.querySelector('[data-cz-grid]');
    if (!grid) return;
    Array.from(grid.querySelectorAll('.cz-cell')).forEach(initCard);

    // sticky toolbar offset under the theme header
    const header = document.querySelector('.header');
    const setHead = () => root.style.setProperty('--cz-head', ((header && header.offsetHeight) || 0) + 'px');
    setHead(); window.addEventListener('resize', setHead);

    // load more (button + automatic when it comes into view)
    const btn = root.querySelector('[data-cz-more-btn]');
    const shown = root.querySelector('[data-cz-shown]');
    const bar = root.querySelector('[data-cz-progress]');
    const total = parseInt(root.dataset.total || '0', 10);
    let loading = false;
    async function loadMore() {
      if (!btn || loading || !btn.dataset.next) return;
      loading = true; btn.classList.add('is-loading'); btn.disabled = true;
      try {
        const url = btn.dataset.next + (btn.dataset.next.includes('?') ? '&' : '?') + 'section_id=' + encodeURIComponent(root.dataset.section);
        const res = await fetch(url, { credentials: 'same-origin' });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const cells = Array.from(doc.querySelectorAll('[data-cz-grid] > .cz-cell'));
        const start = grid.querySelectorAll('.cz-cell').length;
        cells.forEach((c, i) => { const n = document.importNode(c, true); grid.appendChild(n); initCard(n, start + i); });
        const count = grid.querySelectorAll('.cz-cell').length;
        if (shown) shown.textContent = count;
        if (bar && total) bar.style.width = Math.min(100, (count / total) * 100).toFixed(1) + '%';
        const nextBtn = doc.querySelector('[data-cz-more-btn]');
        if (nextBtn && nextBtn.dataset.next) btn.dataset.next = nextBtn.dataset.next;
        else { btn.dataset.next = ''; btn.closest('.cz-more').classList.add('is-done'); }
      } catch (err) {
        window.location.href = btn.dataset.next;
      } finally {
        loading = false; btn.classList.remove('is-loading'); btn.disabled = false;
      }
    }
    if (btn) {
      btn.addEventListener('click', loadMore);
      if ('IntersectionObserver' in window && root.dataset.auto === 'true') {
        new IntersectionObserver((en) => { if (en[0].isIntersecting) loadMore(); }, { rootMargin: '400px 0px' }).observe(btn);
      }
    }
  }

  function boot(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-cz-sky]').forEach(initSky);
    scope.querySelectorAll('[data-cz-hero]').forEach(initHero);
    scope.querySelectorAll('[data-cz-grid-root]').forEach(initGrid);
  }
  window.__czBoot = () => boot(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document)); else boot(document);
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
